import { componentApi } from '@ardoq/api';
import { isArdoqError } from '@ardoq/common-helpers';
import { EnhancedScopeData } from '@ardoq/data-model';
import { I18nLocale, t } from '@ardoq/i18n';
import { logError } from '@ardoq/sentry';
import {
  getLoadComponentSuggestionsAsync,
  LabelAndValue,
} from '@ardoq/suggestions-api';
import React, { useState, useEffect, useCallback } from 'react';
import { IFilterParams } from 'ag-grid-enterprise';
import {
  APIComponentAttributes,
  APIScopeData,
  ArdoqId,
} from '@ardoq/api-types';
import { AsyncMultiselect } from '@ardoq/select';
import { InventoryContext } from '../types';
import { SetFilterEntry } from './types';

type DefaultOptionsGroup = { label: string; options: LabelAndValue[] };

export type CustomComponentSelectionFilterProps = IFilterParams<
  APIComponentAttributes,
  InventoryContext
> & {
  getDefaultOptionsGroup: (i18nLocale: I18nLocale) => DefaultOptionsGroup;
  isSpecialValue: (value: string) => boolean;
  getResolveSpecialFilterValue: (
    i18nLocale: I18nLocale
  ) => (value: string) => LabelAndValue;
  workspaceIdsScopeForSuggestions: ArdoqId[];
};

const getUnresolvedValues = (
  values: ArdoqId[],
  scopeData: EnhancedScopeData,
  isSpecialValue: (value: string) => boolean
): ArdoqId[] =>
  values.filter(
    (id: ArdoqId) =>
      !isSpecialValue(id) && scopeData.componentsById[id] === undefined
  );

const mapScopeDataToComponentIdAndNameMap = (
  scopeData: APIScopeData
): Map<ArdoqId, string> => {
  return new Map(scopeData.components.map(comp => [comp._id, comp.name]));
};

const resolveComponentValue = (
  value: string,
  scopeData: EnhancedScopeData,
  fetchedComponentsMap: Map<ArdoqId, string>,
  i18nLocale: I18nLocale
): LabelAndValue => {
  const component = scopeData.componentsById[value];
  if (component) {
    return { label: component.name, value: value };
  }

  const fetchedLabel = fetchedComponentsMap.get(value);
  if (fetchedLabel) {
    return { label: fetchedLabel, value: value };
  }

  return {
    label: t(
      i18nLocale,
      'inventoryApp:ag.filter.componentFallbackLabel',
      { id: value },
      'Component ({{id}})'
    ),
    value: value,
  };
};

const resolveFilterValue = (
  value: string,
  scopeData: EnhancedScopeData,
  fetchedComponentsMap: Map<ArdoqId, string>,
  isSpecialValue: (value: string) => boolean,
  resolveSpecialFilterValue: (value: string) => LabelAndValue,
  i18nLocale: I18nLocale
): LabelAndValue => {
  if (isSpecialValue(value)) {
    return resolveSpecialFilterValue(value);
  }

  return resolveComponentValue(
    value,
    scopeData,
    fetchedComponentsMap,
    i18nLocale
  );
};

const fetchUnresolvedComponents = async (
  unresolvedValues: ArdoqId[]
): Promise<Map<ArdoqId, string>> => {
  if (unresolvedValues.length === 0) {
    return new Map();
  }

  const fetchedComponents = await componentApi.scopedata({
    entityIds: unresolvedValues,
  });
  if (isArdoqError(fetchedComponents)) {
    return new Map();
  }
  return mapScopeDataToComponentIdAndNameMap(fetchedComponents);
};

export const CustomComponentSelectionFilter = ({
  getDefaultOptionsGroup,
  getResolveSpecialFilterValue,
  isSpecialValue,
  api,
  column,
  context,
  workspaceIdsScopeForSuggestions,
}: CustomComponentSelectionFilterProps) => {
  const colId = column.getColId();
  const [selectedValues, setSelectedValues] = useState<LabelAndValue[]>([]);
  const [key, setKey] = useState(0);
  // AG Grid external-lib callback: locale is propagated via context mutation
  // from Inventory.tsx and read fresh on each render. Do NOT add i18nLocale
  // to any hook dep array on this surface (see apps/ardoq-front/AGENTS.md).
  const i18nLocale = context?.i18nLocale ?? {};

  const fetchSuggestions = useCallback(
    (searchText: string) =>
      getLoadComponentSuggestionsAsync({
        workspaceIds: workspaceIdsScopeForSuggestions,
      })(searchText),
    [workspaceIdsScopeForSuggestions]
  );

  const resolveInitialValues = useCallback(async (): Promise<void> => {
    const scopeData = api.getGridOption('context')
      .scopeData as EnhancedScopeData;
    const localeAtInvocation = context?.i18nLocale ?? {};
    const columnFilterModel = api.getColumnFilterModel<SetFilterEntry>(colId);
    const initialValues = (columnFilterModel?.values ?? []) as string[];

    if (initialValues.length === 0) {
      return;
    }

    const unresolvedValues = getUnresolvedValues(
      initialValues,
      scopeData,
      isSpecialValue
    );
    const fetchedComponentsMap =
      await fetchUnresolvedComponents(unresolvedValues);

    const resolveSpecial = getResolveSpecialFilterValue(localeAtInvocation);
    const resolvedValues = initialValues.map(value =>
      resolveFilterValue(
        value,
        scopeData,
        fetchedComponentsMap,
        isSpecialValue,
        resolveSpecial,
        localeAtInvocation
      )
    );

    setSelectedValues(resolvedValues);
  }, [api, colId, context, isSpecialValue, getResolveSpecialFilterValue]);

  useEffect(() => {
    const handleFilterOpened = () => {
      resolveInitialValues().catch(() => {
        logError(
          new Error('Failed to resolve initial reference filter values')
        );
      });
      // Force AsyncMultiselect to re-render and reload options
      setKey(prev => prev + 1);
    };

    const handleFilterChanged = () => {
      const columnFilterModel = api.getColumnFilterModel<SetFilterEntry>(colId);
      const filterValues = (columnFilterModel?.values as string[]) ?? [];

      const selectedValueIds = selectedValues.map(v => v.value);
      const hasChanged =
        filterValues.length !== selectedValueIds.length ||
        !filterValues.every(id => selectedValueIds.includes(id));

      if (hasChanged) {
        if (filterValues.length === 0) {
          setSelectedValues([]);
        } else {
          resolveInitialValues();
        }
      }
    };

    if (api && typeof api.addEventListener === 'function') {
      api.addEventListener('filterOpened', handleFilterOpened);
      api.addEventListener('filterChanged', handleFilterChanged);
    }

    return () => {
      if (api && typeof api.removeEventListener === 'function') {
        api.removeEventListener('filterOpened', handleFilterOpened);
        api.removeEventListener('filterChanged', handleFilterChanged);
      }
    };
  }, [api, resolveInitialValues, colId, selectedValues]);

  const loadOptions = useCallback(
    async (searchText: string) => {
      const localeAtInvocation = context?.i18nLocale ?? {};
      const optionsGroup = getDefaultOptionsGroup(localeAtInvocation);
      try {
        const suggestions = await fetchSuggestions(searchText);

        return [
          optionsGroup,
          {
            label: t(
              localeAtInvocation,
              'inventoryApp:ag.filter.componentsGroupLabel',
              'Components'
            ),
            options: suggestions,
          },
        ];
      } catch (error) {
        return [optionsGroup];
      }
    },
    [context, getDefaultOptionsGroup, fetchSuggestions]
  );

  const handleValueChange = async (values: any) => {
    const newValues = (values || []) as LabelAndValue[];

    setSelectedValues(newValues);

    const nextFilter =
      newValues.length > 0
        ? ({
            values: newValues.map(v => v.value),
            filterType: 'set',
          } satisfies SetFilterEntry)
        : null;

    await api.setColumnFilterModel(colId, nextFilter);
    api.onFilterChanged();
  };

  return (
    <div style={{ padding: '10px', minWidth: '400px' }}>
      <AsyncMultiselect<LabelAndValue>
        i18nLocale={i18nLocale}
        placeholder={t(
          i18nLocale,
          'inventoryApp:ag.filter.searchPlaceholder',
          'Search components by name'
        )}
        aria-label={t(
          i18nLocale,
          'inventoryApp:ag.filter.searchPlaceholder',
          'Search components by name'
        )}
        loadOptions={loadOptions}
        key={key}
        defaultOptions={true}
        value={selectedValues}
        menuIsOpen={true}
        onChange={handleValueChange}
        autoFocus={true}
      />
    </div>
  );
};
